<template>
    <!-- Loading -->
    <div class="section text-center mt-3" v-if="loading">
        <div class="spinner-border text-secondary" role="status"></div>
    </div>

    <div v-if="!loading && event" class="event-tracking-page">

        <!-- Event Header Card -->
        <div class="section tracking-block">
            <div class="goals">
                <div class="meeting-card">
                    <div class="meeting-link-static">
                        <div class="in">
                            <div class="meeting-header-compact">
                                <h3 class="meeting-title-compact">{{ event.name }}</h3>
                                <span class="badge" :class="levelBadgeClass(event.event_level)">
                                    {{ formatLevel(event.event_level) }}
                                </span>
                            </div>
                            <div class="meeting-info-compact mt-1">
                                <span class="info-item" v-if="event.date_from">
                                    <ion-icon name="calendar-outline"></ion-icon>
                                    {{ event.date_from }}
                                    <span v-if="event.date_to"> – {{ event.date_to }}</span>
                                </span>
                                <span class="info-item" v-else-if="event.date">
                                    <ion-icon name="calendar-outline"></ion-icon>
                                    {{ event.date }}
                                    <span v-if="event.time"> {{ event.time }}</span>
                                </span>
                                <span class="info-separator" v-if="event.org_level">/</span>
                                <span class="info-item" v-if="event.org_level">
                                    <ion-icon name="people-outline"></ion-icon>
                                    {{ formatOrgLevel(event.org_level) }}
                                </span>
                            </div>
                            <div class="meeting-location-compact" v-if="event.place">
                                <ion-icon name="location-outline"></ion-icon>
                                <span>{{ event.place }}</span>
                            </div>
                            <div class="mt-1 text-muted small" v-if="event.description">
                                {{ event.description }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- No sections configured yet (admin view) -->
        <div class="section mt-2" v-if="isAdmin && !sections.length">
            <div class="alert-box">
                <ion-icon name="information-circle-outline"></ion-icon>
                <span>No sections configured yet. Please set up sections from the admin portal, then assign conveners here.</span>
            </div>
        </div>

        <!-- Not assigned to any section (convener view) -->
        <div class="section mt-2" v-if="!isAdmin && !sections.length">
            <div class="alert-box alert-box-info">
                <ion-icon name="person-circle-outline"></ion-icon>
                <span>You have not been assigned as a convener to any section of this event yet.</span>
            </div>
        </div>

        <!-- Section Tabs -->
        <div class="section tracking-block meetings-tabs-wrap" v-if="sections.length">
            <div class="tabs-scroll-wrapper">
                <ul class="nav nav-tabs capsuled meetings-tabs" role="tablist">
                    <li class="nav-item" v-for="section in sections" :key="section.id">
                        <a class="nav-link" :class="{ active: activeSection === section.id }"
                            href="javascript:void(0)" role="tab"
                            @click="activeSection = section.id">
                            {{ section.section_number }}. {{ section.name }}
                        </a>
                    </li>
                </ul>
            </div>
        </div>

        <!-- Active Section Content -->
        <div v-for="section in sections" :key="'content-' + section.id">
            <div v-if="activeSection === section.id">

                <!-- Convener Card -->
                <div class="section tracking-block mt-1">
                    <div class="section-heading">
                        <h4>Convener
                            <span class="text-muted" style="font-size:12px;">({{ section.name }})</span>
                        </h4>
                        <!-- Admin-only: assign / change convener button -->
                        <a href="javascript:void(0)" class="link" v-if="permissions.assign_convener"
                            @click="openAssignConvener(section)">
                            {{ section.convener_name ? 'Change' : '+ Assign' }}
                        </a>
                    </div>
                    <ul class="listview image-listview text inset">
                        <!-- Convener assigned -->
                        <li v-if="section.convener_name">
                            <div class="item">
                                <div class="icon-box bg-primary">
                                    <ion-icon name="person-outline"></ion-icon>
                                </div>
                                <div class="in">
                                    <div>
                                        <strong>{{ section.convener_name }}</strong>
                                        <div class="text-muted small">{{ section.convener_mobile }}</div>
                                    </div>
                                    <!-- Admin can remove -->
                                    <button class="btn btn-sm btn-outline-danger"
                                        v-if="permissions.assign_convener"
                                        @click="doRemoveConvener(section)">
                                        <ion-icon name="trash-outline"></ion-icon>
                                    </button>
                                </div>
                            </div>
                        </li>
                        <!-- No convener yet -->
                        <li v-else>
                            <div class="item text-muted small">
                                <ion-icon name="alert-circle-outline" class="me-2 text-warning"></ion-icon>
                                <span v-if="permissions.assign_convener">
                                    Not assigned — tap <strong>+ Assign</strong> above to set a convener
                                </span>
                                <span v-else>
                                    Not assigned yet — contact your admin
                                </span>
                            </div>
                        </li>
                    </ul>
                </div>

                <!-- Section Duties (read-only from admin config) -->
                <div class="section tracking-block mt-1" v-if="section.duties && section.duties.length">
                    <div class="section-heading">
                        <h4>Section Duties</h4>
                        <span class="badge bg-light text-muted small">Admin configured</span>
                    </div>
                    <ul class="listview image-listview text inset">
                        <li v-for="duty in section.duties" :key="duty.id">
                            <div class="item">
                                <div class="icon-box bg-warning">
                                    <ion-icon name="checkmark-outline"></ion-icon>
                                </div>
                                <div class="in">
                                    <div>
                                        {{ duty.duty }}
                                        <div class="text-muted small" v-if="duty.description">{{ duty.description }}</div>
                                    </div>
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>

                <!-- Task Managers -->
                <div class="section tracking-block mt-1">
                    <div class="section-heading">
                        <h4>Task Managers</h4>
                        <a href="javascript:void(0)" class="link" v-if="permissions.add_task_manager"
                            @click="openAddTaskManager(section)">+ Add</a>
                    </div>
                    <ul class="listview image-listview text inset" v-if="section.task_managers && section.task_managers.length">
                        <li v-for="tm in section.task_managers" :key="tm.id">
                            <div class="item">
                                <div class="icon-box bg-info">
                                    <ion-icon name="briefcase-outline"></ion-icon>
                                </div>
                                <div class="in">
                                    <div>
                                        <strong>{{ tm.member_name }}</strong>
                                        <div class="text-muted small">{{ tm.member_mobile }}</div>
                                    </div>
                                    <button class="btn btn-sm btn-outline-danger"
                                        v-if="permissions.add_task_manager"
                                        @click="deleteTaskManager(tm)">
                                        <ion-icon name="trash-outline"></ion-icon>
                                    </button>
                                </div>
                            </div>
                        </li>
                    </ul>
                    <div class="empty-row" v-else>No task managers added</div>
                </div>

                <!-- Wings -->
                <div class="section tracking-block mt-1">
                    <div class="section-heading">
                        <h4>Wings</h4>
                        <span class="badge bg-light text-muted small">Admin configured</span>
                    </div>

                    <div v-if="section.wings && section.wings.length">
                        <div v-for="wing in section.wings" :key="wing.id" class="wing-block">
                            <!-- Wing Header -->
                            <div class="wing-header" @click="toggleWing(wing.id)">
                                <div class="wing-header-left">
                                    <span class="wing-code">{{ wing.code }}</span>
                                    <span class="wing-name">{{ wing.name }}</span>
                                </div>
                                <div class="wing-header-right">
                                    <span class="badge bg-light text-dark me-1">
                                        {{ wing.volunteers ? wing.volunteers.length : 0 }} volunteers
                                    </span>
                                    <ion-icon :name="openWings.includes(wing.id) ? 'chevron-up-outline' : 'chevron-down-outline'"></ion-icon>
                                </div>
                            </div>

                            <!-- Wing Content (collapsible) -->
                            <div v-if="openWings.includes(wing.id)" class="wing-content">

                                <!-- Wing Duties (read-only) -->
                                <div class="wing-sub-section" v-if="wing.duties && wing.duties.length">
                                    <div class="wing-sub-heading">
                                        <span>Duties</span>
                                        <span class="badge bg-light text-muted" style="font-size:10px;">Admin configured</span>
                                    </div>
                                    <ul class="listview image-listview text inset">
                                        <li v-for="duty in wing.duties" :key="duty.id">
                                            <div class="item">
                                                <div class="icon-box bg-warning" style="min-width:32px;height:32px;">
                                                    <ion-icon name="clipboard-outline"></ion-icon>
                                                </div>
                                                <div class="in small">
                                                    {{ duty.duty }}
                                                    <span class="text-muted" v-if="duty.description"> – {{ duty.description }}</span>
                                                </div>
                                            </div>
                                        </li>
                                    </ul>
                                </div>

                                <!-- Wing Volunteers -->
                                <div class="wing-sub-section mt-1">
                                    <div class="wing-sub-heading">
                                        <span>Volunteers</span>
                                        <a href="javascript:void(0)" class="link small"
                                            v-if="permissions.add_volunteer"
                                            @click="openAddVolunteer(wing)">+ Add</a>
                                    </div>
                                    <ul class="listview image-listview text inset"
                                        v-if="wing.volunteers && wing.volunteers.length">
                                        <li v-for="vol in wing.volunteers" :key="vol.id">
                                            <div class="item">
                                                <div class="icon-box bg-success" style="min-width:32px;height:32px;">
                                                    <ion-icon name="person-outline"></ion-icon>
                                                </div>
                                                <div class="in small">
                                                    <div>
                                                        <strong>{{ vol.member_name }}</strong>
                                                        <div class="text-muted" style="font-size:11px;">{{ vol.member_mobile }}</div>
                                                    </div>
                                                    <button class="btn btn-sm btn-outline-danger"
                                                        v-if="permissions.remove_volunteer"
                                                        @click="deleteVolunteer(vol)">
                                                        <ion-icon name="trash-outline"></ion-icon>
                                                    </button>
                                                </div>
                                            </div>
                                        </li>
                                    </ul>
                                    <div class="empty-row" v-else>No volunteers added</div>
                                </div>

                            </div>
                        </div>
                    </div>
                    <div class="empty-row" v-else>
                        <ion-icon name="alert-circle-outline" class="text-warning me-1"></ion-icon>
                        No wings configured — set up wings from admin portal
                    </div>
                </div>

            </div>
        </div>
    </div>

    <!-- ─── Assign Convener — bottom action sheet (app standard) ───────── -->
    <div class="modal fade action-sheet" id="assignConvenerModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">
                        Assign Convener
                        <span class="text-primary" v-if="selectedSection"> – {{ selectedSection.name }}</span>
                    </h5>
                </div>
                <div class="modal-body">
                    <div class="action-sheet-content event-tracking-member-sheet">
                        <div class="form-group boxed mb-2">
                            <div class="input-wrapper">
                                <label class="label">Search Member (name / mobile)</label>
                                <input type="text" class="form-control" v-model="memberSearch"
                                    @input="searchMembers" placeholder="Type to search..." />
                            </div>
                        </div>
                        <div class="text-center py-2" v-if="memberSearchLoading">
                            <div class="spinner-border spinner-border-sm text-secondary"></div>
                        </div>
                        <ul class="listview image-listview text inset mt-1 mb-2" v-if="memberResults.length">
                            <li v-for="m in memberResults" :key="m.id">
                                <a href="javascript:void(0)" class="item" @click="selectConvener(m)">
                                    <div class="icon-box bg-primary">
                                        <ion-icon name="person-outline"></ion-icon>
                                    </div>
                                    <div class="in">
                                        <div>
                                            <strong>{{ m.name }}</strong>
                                            <div class="text-muted small">{{ m.mobile }}</div>
                                        </div>
                                        <ion-icon name="checkmark-circle-outline" class="text-primary"></ion-icon>
                                    </div>
                                </a>
                            </li>
                        </ul>
                        <div class="text-muted small text-center py-2"
                            v-if="memberSearch.length >= 2 && !memberResults.length && !memberSearchLoading">
                            No members found
                        </div>
                        <button type="button" class="btn btn-outline-secondary btn-block btn-lg w-100" data-bs-dismiss="modal">
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- ─── Add Task Manager — bottom action sheet ──────────────────────── -->
    <div class="modal fade action-sheet" id="addTaskManagerModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Add Task Manager
                        <span class="text-primary" v-if="selectedSection"> – {{ selectedSection.name }}</span>
                    </h5>
                </div>
                <div class="modal-body">
                    <div class="action-sheet-content event-tracking-member-sheet">
                        <div class="form-group boxed mb-2">
                            <div class="input-wrapper">
                                <label class="label">Search Member (name / mobile)</label>
                                <input type="text" class="form-control" v-model="memberSearch"
                                    @input="searchMembers" placeholder="Type to search..." />
                            </div>
                        </div>
                        <div class="text-center py-2" v-if="memberSearchLoading">
                            <div class="spinner-border spinner-border-sm text-secondary"></div>
                        </div>
                        <ul class="listview image-listview text inset mt-1 mb-2" v-if="memberResults.length">
                            <li v-for="m in memberResults" :key="m.id">
                                <a href="javascript:void(0)" class="item" @click="selectTaskManager(m)">
                                    <div class="icon-box bg-info">
                                        <ion-icon name="person-outline"></ion-icon>
                                    </div>
                                    <div class="in">
                                        <div>
                                            <strong>{{ m.name }}</strong>
                                            <div class="text-muted small">{{ m.mobile }}</div>
                                        </div>
                                        <ion-icon name="add-circle-outline" class="text-primary"></ion-icon>
                                    </div>
                                </a>
                            </li>
                        </ul>
                        <div class="text-muted small text-center py-2"
                            v-if="memberSearch.length >= 2 && !memberResults.length && !memberSearchLoading">
                            No members found
                        </div>
                        <button type="button" class="btn btn-outline-secondary btn-block btn-lg w-100" data-bs-dismiss="modal">
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- ─── Add Volunteer — bottom action sheet ─────────────────────────── -->
    <div class="modal fade action-sheet" id="addVolunteerModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Add Volunteer
                        <span class="text-primary" v-if="selectedWing"> – {{ selectedWing.code }} {{ selectedWing.name }}</span>
                    </h5>
                </div>
                <div class="modal-body">
                    <div class="action-sheet-content event-tracking-member-sheet">
                        <div class="form-group boxed mb-2">
                            <div class="input-wrapper">
                                <label class="label">Search Member (name / mobile)</label>
                                <input type="text" class="form-control" v-model="memberSearch"
                                    @input="searchMembers" placeholder="Type to search..." />
                            </div>
                        </div>
                        <div class="text-center py-2" v-if="memberSearchLoading">
                            <div class="spinner-border spinner-border-sm text-secondary"></div>
                        </div>
                        <ul class="listview image-listview text inset mt-1 mb-2" v-if="memberResults.length">
                            <li v-for="m in memberResults" :key="m.id">
                                <a href="javascript:void(0)" class="item" @click="selectVolunteer(m)">
                                    <div class="icon-box bg-success">
                                        <ion-icon name="person-outline"></ion-icon>
                                    </div>
                                    <div class="in">
                                        <div>
                                            <strong>{{ m.name }}</strong>
                                            <div class="text-muted small">{{ m.mobile }}</div>
                                        </div>
                                        <ion-icon name="add-circle-outline" class="text-success"></ion-icon>
                                    </div>
                                </a>
                            </li>
                        </ul>
                        <div class="text-muted small text-center py-2"
                            v-if="memberSearch.length >= 2 && !memberResults.length && !memberSearchLoading">
                            No members found
                        </div>
                        <button type="button" class="btn btn-outline-secondary btn-block btn-lg w-100" data-bs-dismiss="modal">
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Toast -->
    <div class="toast-container position-fixed bottom-0 end-0 p-3" style="z-index:9999;">
        <div class="toast align-items-center text-bg-success border-0" id="successToast" role="alert">
            <div class="d-flex">
                <div class="toast-body">{{ toastMessage }}</div>
                <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"></button>
            </div>
        </div>
        <div class="toast align-items-center text-bg-danger border-0" id="errorToast" role="alert">
            <div class="d-flex">
                <div class="toast-body">{{ toastMessage }}</div>
                <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"></button>
            </div>
        </div>
    </div>
</template>

<script>
import $ from "jquery";
import UserService from "../../services/user.service";

export default {
    name: "EventTracking",
    data() {
        return {
            loading: true,
            event: null,
            sections: [],
            permissions: {
                is_admin: false,
                assign_convener: false,
                add_task_manager: false,
                add_volunteer: false,
                remove_volunteer: false,
            },
            isAdmin: false,
            activeSection: null,
            openWings: [],

            // Shared member search
            memberSearch: "",
            memberSearchLoading: false,
            memberResults: [],
            memberSearchTimer: null,

            selectedSection: null,
            selectedWing: null,

            toastMessage: "",
        };
    },
    mounted() {
        this.eventId = this.$route.params.event_id || this.$route.params.id;
        this.fetchDetail();
    },
    methods: {
        // ── Data ──────────────────────────────────────────────────────────────

        fetchDetail() {
            this.loading = true;
            UserService.authPostRequest("get-event-tracking-detail", { event_id: this.eventId }).then(
                (response) => {
                    if (response.data.status === "success") {
                        this.event      = response.data.data.event;
                        this.sections   = response.data.data.sections;
                        this.permissions = response.data.data.permissions;
                        this.isAdmin    = response.data.data.is_admin;
                        if (this.sections.length) {
                            this.activeSection = this.sections[0].id;
                        }
                    }
                    this.loading = false;
                },
                () => { this.loading = false; }
            );
        },

        // ── Wing collapse ─────────────────────────────────────────────────────

        toggleWing(wingId) {
            const idx = this.openWings.indexOf(wingId);
            if (idx >= 0) this.openWings.splice(idx, 1);
            else this.openWings.push(wingId);
        },

        // ── Member search ─────────────────────────────────────────────────────

        searchMembers() {
            clearTimeout(this.memberSearchTimer);
            if (this.memberSearch.length < 2) {
                this.memberResults = [];
                return;
            }
            this.memberSearchTimer = setTimeout(() => {
                this.memberSearchLoading = true;
                UserService.authPostRequest("search-event-members", { search: this.memberSearch }).then(
                    (res) => {
                        if (res.data.status === "success") {
                            this.memberResults = res.data.data.members;
                        }
                        this.memberSearchLoading = false;
                    },
                    () => { this.memberSearchLoading = false; }
                );
            }, 400);
        },

        resetMemberSearch() {
            this.memberSearch  = "";
            this.memberResults = [];
        },

        // ── Convener ──────────────────────────────────────────────────────────

        openAssignConvener(section) {
            this.selectedSection = section;
            this.resetMemberSearch();
            $("#assignConvenerModal").modal("show");
        },

        selectConvener(member) {
            UserService.authPostRequest("assign-section-convener", {
                section_id: this.selectedSection.id,
                member_id: member.id,
            }).then((res) => {
                if (res.data.status === "success") {
                    const sec = this.sections.find(s => s.id === this.selectedSection.id);
                    if (sec) {
                        sec.convener_member_id = res.data.data.convener_member_id;
                        sec.convener_name      = res.data.data.convener_name;
                        sec.convener_mobile    = res.data.data.convener_mobile;
                    }
                    $("#assignConvenerModal").modal("hide");
                    this.showToast("Convener assigned successfully", "success");
                } else {
                    this.showToast(res.data.message || "Failed to assign convener", "error");
                }
            });
        },

        doRemoveConvener(section) {
            if (!confirm("Remove convener from this section?")) return;
            UserService.authPostRequest("remove-section-convener", {
                section_id: section.id,
            }).then((res) => {
                if (res.data.status === "success") {
                    const sec = this.sections.find(s => s.id === section.id);
                    if (sec) {
                        sec.convener_member_id = null;
                        sec.convener_name      = null;
                        sec.convener_mobile    = null;
                    }
                    this.showToast("Convener removed", "success");
                }
            });
        },

        // ── Task Managers ─────────────────────────────────────────────────────

        openAddTaskManager(section) {
            this.selectedSection = section;
            this.resetMemberSearch();
            $("#addTaskManagerModal").modal("show");
        },

        selectTaskManager(member) {
            UserService.authPostRequest("store-section-task-manager", {
                section_id: this.selectedSection.id,
                member_id: member.id,
            }).then((res) => {
                if (res.data.status === "success") {
                    const sec = this.sections.find(s => s.id === this.selectedSection.id);
                    if (sec) sec.task_managers.push(res.data.data.task_manager);
                    $("#addTaskManagerModal").modal("hide");
                    this.showToast("Task manager added", "success");
                } else {
                    this.showToast(res.data.message, "error");
                }
            });
        },

        deleteTaskManager(tm) {
            if (!confirm("Remove this task manager?")) return;
            UserService.authPostRequest("delete-section-task-manager", { task_manager_id: tm.id }).then(
                (res) => {
                    if (res.data.status === "success") {
                        this.sections.forEach(sec => {
                            sec.task_managers = sec.task_managers.filter(t => t.id !== tm.id);
                        });
                        this.showToast("Task manager removed", "success");
                    }
                }
            );
        },

        // ── Wing Volunteers ───────────────────────────────────────────────────

        openAddVolunteer(wing) {
            this.selectedWing = wing;
            this.resetMemberSearch();
            $("#addVolunteerModal").modal("show");
        },

        selectVolunteer(member) {
            UserService.authPostRequest("store-wing-volunteer", {
                wing_id: this.selectedWing.id,
                member_id: member.id,
            }).then((res) => {
                if (res.data.status === "success") {
                    this.sections.forEach(sec => {
                        const w = sec.wings?.find(w => w.id === this.selectedWing.id);
                        if (w) w.volunteers.push(res.data.data.volunteer);
                    });
                    $("#addVolunteerModal").modal("hide");
                    this.showToast("Volunteer added", "success");
                } else {
                    this.showToast(res.data.message, "error");
                }
            });
        },

        deleteVolunteer(vol) {
            if (!confirm("Remove this volunteer?")) return;
            UserService.authPostRequest("delete-wing-volunteer", { volunteer_id: vol.id }).then(
                (res) => {
                    if (res.data.status === "success") {
                        this.sections.forEach(sec => {
                            sec.wings?.forEach(w => {
                                w.volunteers = w.volunteers.filter(v => v.id !== vol.id);
                            });
                        });
                        this.showToast("Volunteer removed", "success");
                    }
                }
            );
        },

        // ── Helpers ───────────────────────────────────────────────────────────

        showToast(message, type = "success") {
            this.toastMessage = message;
            const el = document.getElementById(type === "success" ? "successToast" : "errorToast");
            if (el) new window.bootstrap.Toast(el, { delay: 2500 }).show();
        },

        formatLevel(level) {
            const map = { state: "State", district: "District", mandalam: "Mandalam", unit: "Unit" };
            return map[level] || level;
        },

        formatOrgLevel(level) {
            const map = {
                organisation: "Organisation", youth: "Youth", students: "Students",
                women: "Women", girls: "Girls", balavedi: "Balavedi", all: "ALL",
            };
            return map[level] || level;
        },

        levelBadgeClass(level) {
            const map = { state: "bg-danger", district: "bg-primary", mandalam: "bg-success", unit: "bg-warning text-dark" };
            return map[level] || "bg-secondary";
        },
    },
};
</script>

<style scoped>
.meeting-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,.08);
    margin: 0 16px;
    overflow: hidden;
}
.meeting-link-static { padding: 14px 16px; }
.meeting-header-compact {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
}
.meeting-title-compact { font-size: 16px; font-weight: 700; color: #1a1a2e; margin: 0; flex: 1; }
.meeting-info-compact { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.info-item { display: flex; align-items: center; gap: 3px; font-size: 12px; color: #6c757d; }
.info-separator { color: #ccc; }
.meeting-location-compact { display: flex; align-items: center; gap: 4px; font-size: 12px; color: #6c757d; margin-top: 4px; }

.tracking-block { padding: 0 16px; }
.tabs-scroll-wrapper { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.meetings-tabs { white-space: nowrap; flex-wrap: nowrap; }

.section-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
}
.section-heading h4 { font-size: 14px; font-weight: 600; margin: 0; }
.section-heading .link { font-size: 13px; color: #007bff; }

.alert-box {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 10px;
    padding: 12px 16px;
    font-size: 13px;
    color: #856404;
    margin: 0 16px;
}
.alert-box-info {
    background: #e8f4fd;
    border-color: #90caf9;
    color: #1565c0;
}
.alert-box ion-icon { font-size: 20px; flex-shrink: 0; }

.empty-row {
    font-size: 12px;
    color: #aaa;
    text-align: center;
    padding: 8px 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

/* Wing block */
.wing-block {
    margin: 0 0 8px;
    border: 1px solid #e9ecef;
    border-radius: 10px;
    overflow: hidden;
}
.wing-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 11px 14px;
    background: #f8f9fa;
    cursor: pointer;
    user-select: none;
}
.wing-header-left { display: flex; align-items: center; gap: 10px; }
.wing-code {
    background: #007bff;
    color: #fff;
    border-radius: 6px;
    padding: 2px 8px;
    font-size: 12px;
    font-weight: 700;
}
.wing-name { font-size: 14px; font-weight: 600; color: #333; }
.wing-header-right { display: flex; align-items: center; gap: 6px; }

.wing-content { padding: 6px 0; }
.wing-sub-section { padding: 0; }
.wing-sub-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 14px;
    font-size: 12px;
    font-weight: 600;
    color: #555;
    text-transform: uppercase;
    letter-spacing: .4px;
}
.wing-sub-heading .link { font-size: 12px; color: #007bff; text-transform: none; font-weight: 400; }

.icon-box {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    min-width: 36px;
    height: 36px;
}
.icon-box ion-icon { color: #fff; font-size: 16px; }

/* Member pickers: bottom action sheet, ~half viewport, scroll inside (matches app action-sheet pattern) */
.event-tracking-member-sheet {
    max-height: min(50vh, 420px) !important;
    overflow-y: auto;
    padding-bottom: 8px;
}
</style>
